/**
 * @fileoverview useDictatedField — voice dictation for a plain text field
 * (MOR-1456).
 *
 * Purpose:
 * - Gives any controlled `<Input>` / `<Textarea>` the chat composer's
 *   dictation behaviour without each surface re-deriving it: the live
 *   transcript shows in the field while the mic is open, the field is
 *   read-only until the final transcript lands, and the final transcript is
 *   APPENDED to whatever was typed (never replaces it).
 * - The mic button itself is `DictationButton`; this hook feeds it.
 *
 * Used by:
 * - src/components/timesheets/QuickTimeEntryDialog.tsx (narrative)
 * - src/components/timesheets/WeeklyTimesheet.tsx (compose-row narrative)
 * - src/components/timesheets/EntryDetailSheet.tsx (narrative)
 * - src/components/timesheets/NlEntryComposer.tsx (the plain-English box)
 */

import { useRef } from "react";
import {
  appendTranscript,
  useChatVoiceInput,
  type ChatVoiceInput,
} from "@/ui/hooks/use-chat-voice-input";

export interface UseDictatedFieldOptions {
  /** The field's current value. */
  value: string;
  /** Receives the value with the final transcript appended. */
  onChange: (next: string) => void;
}

export interface DictatedField {
  voice: ChatVoiceInput;
  /**
   * What the field should render: the typed text, plus the live transcript
   * while a session is active.
   */
  displayValue: string;
  /** Typing is held while the mic is open so the transcript lands cleanly. */
  readOnly: boolean;
}

/**
 * Wires dictation into one controlled text field.
 *
 * While the mic is open `displayValue` is the typed text plus the live
 * (interim) transcript and `readOnly` is true; when the final transcript
 * lands it is appended to the field's value THEN via `onChange`, and the
 * field returns to the caller's value. `voice` drives a `DictationButton`.
 */
export function useDictatedField({
  value,
  onChange,
}: UseDictatedFieldOptions): DictatedField {
  // The final transcript arrives asynchronously; append to the value as it
  // is THEN, not as it was when the session started.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const voice = useChatVoiceInput({
    onFinal: (transcript) => {
      onChangeRef.current(appendTranscript(valueRef.current, transcript));
    },
  });

  return {
    voice,
    displayValue: voice.isActive
      ? appendTranscript(value, voice.interim)
      : value,
    readOnly: voice.isActive,
  };
}
